import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  useTheme,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  Legend
} from 'recharts';
import { AnimalMilkingRecord } from './AnimalMilkingHistoryView';

interface MilkProductionEconomicsViewProps {
  records?: AnimalMilkingRecord[];
  animalName?: string;
  animalInfo?: {
    breed?: string;
    birthDate?: string;
    lactationStatus?: string;
    currentWeight?: number;
  };
}

// Helper to calculate Wood's curve: y = a * t^b * e^(-c * t)
// Approximating parameters based on peak yield
const calculateExpectedVolume = (dim: number, peakYield: number) => {
  // Standard parameters for Holstein (can be adjusted)
  const a = peakYield / 2.5; // Scaling factor
  const b = 0.22;
  const c = 0.004;
  return a * Math.pow(Math.max(dim, 1), b) * Math.exp(-c * dim);
};

export const MilkProductionEconomicsView: React.FC<MilkProductionEconomicsViewProps> = ({ 
  records = [], 
  animalName = 'Vaca #3481',
  animalInfo
}) => {
  const theme = useTheme();
  const [projectionPeriod, setProjectionPeriod] = useState<'actual' | 'week' | 'month'>('actual');

  const { displayData, eventRange, projectionStartDim, totalProjectedLoss, periodAverage } = useMemo(() => {
    if (!records || records.length === 0) {
      // Fallback to mock data generator if no records provided
      const mockData = generateCowData();
      return { 
        displayData: mockData, 
        eventRange: { start: 55, end: 73 }, 
        projectionStartDim: null,
        totalProjectedLoss: mockData[mockData.length - 1].cumulativeLoss,
        periodAverage: mockData.reduce((acc, curr) => acc + curr.realVol, 0) / mockData.length
      }; 
    }

    // Sort records by date
    const sortedRecords = [...records].sort((a, b) => 
      new Date(a.handling_date).getTime() - new Date(b.handling_date).getTime()
    );

    if (sortedRecords.length === 0) return { displayData: [], eventRange: null, projectionStartDim: null, totalProjectedLoss: 0, periodAverage: 0 };

    // Estimate Calving Date (start of lactation)
    const firstDate = new Date(sortedRecords[0].handling_date);
    
    // Calculate peak yield from real data to scale the expected curve
    const maxYield = Math.max(...sortedRecords.map(r => r.handling_details.milk_quantity_liters || 0));
    
    let cumulativeLoss = 0;
    const milkPrice = 2.35; // Configurable?

    // 1. Process Historical Data
    const historicalData = sortedRecords.map((record) => {
      const date = new Date(record.handling_date);
      const diffTime = Math.abs(date.getTime() - firstDate.getTime());
      const dim = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
      
      const realVol = record.handling_details.milk_quantity_liters || 0;
      
      // Calculate expected based on our model
      const expectedVol = calculateExpectedVolume(dim, maxYield * 1.05);

      const realRev = realVol * milkPrice;
      const expectedRev = expectedVol * milkPrice;
      const diffRev = realRev - expectedRev;
      
      cumulativeLoss += diffRev;

      // Detect potential "Event" (significant drop)
      const isEvent = realVol < expectedVol * 0.8;

      return {
        dim,
        dateObj: date,
        date: date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        fullDate: date.toLocaleDateString('pt-BR'),
        expectedVol: Number(expectedVol.toFixed(1)),
        realVol: Number(realVol.toFixed(1)),
        expectedRev: Number(expectedRev.toFixed(1)),
        realRev: Number(realRev.toFixed(1)),
        cumulativeLoss: Number(cumulativeLoss.toFixed(1)),
        isEvent,
        isProjection: false,
        notes: record.notes || record.handling_type_code
      };
    });

    // Calculate Average (only from historical data)
    const totalRealVol = historicalData.reduce((sum, d) => sum + d.realVol, 0);
    const periodAverage = historicalData.length > 0 ? totalRealVol / historicalData.length : 0;

    // 2. Process Projection if needed
    let combinedData = [...historicalData];
    let projectionStartDim = null;
    
    if (projectionPeriod !== 'actual' && historicalData.length > 0) {
      const lastRecord = historicalData[historicalData.length - 1];
      projectionStartDim = lastRecord.dim + 1;
      
      // Calculate current trend (average of last 3 records or fewer)
      const recentRecords = historicalData.slice(-3);
      const avgRealVol = recentRecords.reduce((sum, r) => sum + r.realVol, 0) / recentRecords.length;
      
      // Determine projection length
      const projectionDays = projectionPeriod === 'week' ? 7 : 30;
      
      for (let i = 1; i <= projectionDays; i++) {
        const nextDim = lastRecord.dim + i;
        const nextDate = new Date(lastRecord.dateObj);
        nextDate.setDate(nextDate.getDate() + i);
        
        // Expected follows the curve
        const expectedVol = calculateExpectedVolume(nextDim, maxYield * 1.05);
        
        // Real follows the trend (flat projection of recent average)
        // Note: Could also apply a decay rate similar to the curve, but "se continuasse assim" implies steady state
        const projectedRealVol = avgRealVol; 

        const realRev = projectedRealVol * milkPrice;
        const expectedRev = expectedVol * milkPrice;
        const diffRev = realRev - expectedRev;
        
        cumulativeLoss += diffRev;

        combinedData.push({
          dim: nextDim,
          dateObj: nextDate,
          date: nextDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
          fullDate: nextDate.toLocaleDateString('pt-BR'),
          expectedVol: Number(expectedVol.toFixed(1)),
          realVol: Number(projectedRealVol.toFixed(1)),
          expectedRev: Number(expectedRev.toFixed(1)),
          realRev: Number(realRev.toFixed(1)),
          cumulativeLoss: Number(cumulativeLoss.toFixed(1)),
          isEvent: false,
          isProjection: true,
          notes: 'Projeção'
        });
      }
    }

    // Recalculate event range for historical data
    let maxSeq = 0;
    let currentSeq = 0;
    let startIdx = -1;
    let bestStart = -1;

    historicalData.forEach((d) => {
      if (d.isEvent) {
        if (currentSeq === 0) startIdx = d.dim;
        currentSeq++;
      } else {
        if (currentSeq > maxSeq) {
          maxSeq = currentSeq;
          bestStart = startIdx;
        }
        currentSeq = 0;
      }
    });
    if (currentSeq > maxSeq) {
      maxSeq = currentSeq;
      bestStart = startIdx;
    }

    const eventRange = (maxSeq > 3) ? { start: bestStart, end: bestStart + maxSeq } : null;
    const totalProjectedLoss = combinedData[combinedData.length - 1].cumulativeLoss;

    return { displayData: combinedData, eventRange, projectionStartDim, totalProjectedLoss, periodAverage };
  }, [records, projectionPeriod]);

  // Constants
  const MILK_PRICE = 2.35;
  const hasRealData = records.length > 0;
  
  // Stats
  // Calculate average drop during event if event exists
  const avgDrop = eventRange 
    ? displayData
        .filter(d => !d.isProjection && d.dim >= eventRange.start && d.dim <= eventRange.end)
        .reduce((acc, curr) => acc + (curr.realVol - curr.expectedVol), 0) / (eventRange.end - eventRange.start + 1)
    : 0;


  const formatCurrency = (val: number) => 
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(val);

  return (
    <Box sx={{ p: 4, maxWidth: 1400, bgcolor: theme.palette.background.paper, minHeight: '100vh' }}>
      
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.text.primary }}>
            {animalName} - Impacto de eventos na producao e valor
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1, mb: 1 }}>
            {animalInfo?.breed && (
              <Typography variant="body2" color="text.secondary">
                <strong>Raça:</strong> {animalInfo.breed}
              </Typography>
            )}
            {animalInfo?.lactationStatus && (
              <Typography variant="body2" color="text.secondary">
                <strong>Status:</strong> {animalInfo.lactationStatus}
              </Typography>
            )}
            {animalInfo?.currentWeight && (
              <Typography variant="body2" color="text.secondary">
                <strong>Peso:</strong> {animalInfo.currentWeight}kg
              </Typography>
            )}
          </Stack>
          <Typography variant="body1" color="text.secondary">
             {eventRange 
               ? `Evento detectado: Queda de produção (Dia ${eventRange.start} - ${eventRange.end})` 
               : 'Análise de Lactação (Nenhum evento crítico detectado)'}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
            Preco leite: R$ {MILK_PRICE}/L
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
            Periodo analisado: {displayData.length} dias
          </Typography>
        </Box>
      </Box>

      {!hasRealData && (
         <Alert severity="info" sx={{ mb: 4 }}>
           <AlertTitle>Modo de Simulação</AlertTitle>
           Exibindo dados simulados de exemplo. Conecte registros reais de ordenha para ver a análise do animal.
         </Alert>
      )}

      {/* Projection Tabs */}
      <Paper sx={{ mb: 4, width: 'fit-content' }}>
        <Tabs 
          value={projectionPeriod} 
          onChange={(_, val) => setProjectionPeriod(val)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab value="actual" label="Atual" />
          <Tab value="week" label="Projeção (+7 Dias)" />
          <Tab value="month" label="Projeção (+30 Dias)" />
        </Tabs>
      </Paper>

      {/* KPI Cards Row */}
      <Stack direction="row" spacing={4} sx={{ mb: 6, justifyContent: 'space-between' }}>
        
        {/* KPI 1 */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Balanço total {projectionPeriod !== 'actual' ? 'estimado' : 'atual'}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 0.5, color: totalProjectedLoss < 0 ? 'error.main' : 'success.main' }}>
            {formatCurrency(totalProjectedLoss)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            real vs esperado ({projectionPeriod !== 'actual' ? 'com projeção' : 'acumulado'})
          </Typography>
        </Box>

        {/* KPI 2 */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Variação média de produção
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 0.5, color: 'primary.main' }}>
            {avgDrop.toFixed(1)} L/dia
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {eventRange ? 'durante o evento detectado' : 'em todo o período'}
          </Typography>
        </Box>

        {/* KPI 3 */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Pico de Produção
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {Math.max(...displayData.filter(d => !d.isProjection).map(d => d.realVol)).toFixed(1)} L
          </Typography>
          <Typography variant="caption" color="text.secondary">
            máximo atingido
          </Typography>
        </Box>

        {/* KPI 4 */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Status
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {eventRange ? 'Atenção' : 'Normal'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            baseado na curva esperada
          </Typography>
        </Box>
      </Stack>

      {/* Charts Row */}
      <Box sx={{ display: 'flex', gap: 4, mb: 6, height: 350 }}>
        
        {/* Left Chart: Production */}
        <Box sx={{ flex: 1, border: 1, borderColor: theme.palette.divider, p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Producao (L/dia) {hasRealData ? '- Real vs Esperado' : 'com impacto de evento'}
          </Typography>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={displayData} margin={{ top: 5, right: 20, bottom: 20, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={true} stroke={theme.palette.divider} />
              <XAxis 
                dataKey={hasRealData ? "date" : "dim"} 
                label={{ value: hasRealData ? 'Data' : 'DIM', position: 'insideBottom', offset: -10 }} 
                tickLine={false}
                axisLine={true}
                interval={Math.floor(displayData.length / 10)}
              />
              <YAxis 
                label={{ value: 'Litros/dia', angle: -90, position: 'insideLeft' }} 
                domain={[0, 'auto']}
                tickLine={false}
                axisLine={true}
              />
              <Tooltip labelFormatter={(label) => hasRealData ? `Data: ${label}` : `DIM: ${label}`} />
              {eventRange && (
                <ReferenceArea 
                  x1={hasRealData ? displayData.find(d => d.dim === eventRange.start)?.date : eventRange.start} 
                  x2={hasRealData ? displayData.find(d => d.dim === eventRange.end)?.date : eventRange.end} 
                  fill={theme.palette.action.selected} 
                  fillOpacity={0.5} 
                />
              )}
              {projectionStartDim && (
                <ReferenceArea 
                  x1={hasRealData ? displayData.find(d => d.dim === projectionStartDim)?.date : projectionStartDim} 
                  x2={hasRealData ? displayData[displayData.length - 1].date : displayData[displayData.length - 1].dim} 
                  fill={theme.palette.info.light} 
                  fillOpacity={0.1}
                  label="Projeção"
                />
              )}
              <Line type="monotone" dataKey="realVol" stroke={theme.palette.primary.main} strokeWidth={2} dot={false} name="Real/Projetado" />
              <Line type="monotone" dataKey="expectedVol" stroke={theme.palette.secondary.main} strokeDasharray="5 5" strokeWidth={2} dot={false} name="Esperado (Curva)" />
              <ReferenceLine y={periodAverage} stroke={theme.palette.success.main} strokeDasharray="3 3" label={{ value: 'Média', position: 'insideRight', fill: theme.palette.success.main }} />
              <Legend verticalAlign="top" height={36} iconType="plainline" />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        {/* Right Chart: Margin */}
        <Box sx={{ flex: 1, border: 1, borderColor: theme.palette.divider, p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Margem (R$/dia) {hasRealData ? '- Real vs Esperado' : 'com impacto de evento'}
          </Typography>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={displayData} margin={{ top: 5, right: 20, bottom: 20, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={true} stroke={theme.palette.divider} />
              <XAxis 
                dataKey={hasRealData ? "date" : "dim"} 
                label={{ value: hasRealData ? 'Data' : 'DIM', position: 'insideBottom', offset: -10 }} 
                tickLine={false}
                axisLine={true}
                interval={Math.floor(displayData.length / 10)}
              />
              <YAxis 
                label={{ value: 'R$/dia', angle: -90, position: 'insideLeft' }} 
                domain={[0, 'auto']}
                tickLine={false}
                axisLine={true}
              />
              <Tooltip labelFormatter={(label) => hasRealData ? `Data: ${label}` : `DIM: ${label}`} />
              {eventRange && (
                <ReferenceArea 
                  x1={hasRealData ? displayData.find(d => d.dim === eventRange.start)?.date : eventRange.start} 
                  x2={hasRealData ? displayData.find(d => d.dim === eventRange.end)?.date : eventRange.end} 
                  fill={theme.palette.action.selected} 
                  fillOpacity={0.5} 
                />
              )}
              {projectionStartDim && (
                <ReferenceArea 
                  x1={hasRealData ? displayData.find(d => d.dim === projectionStartDim)?.date : projectionStartDim} 
                  x2={hasRealData ? displayData[displayData.length - 1].date : displayData[displayData.length - 1].dim} 
                  fill={theme.palette.info.light} 
                  fillOpacity={0.1}
                  label="Projeção"
                />
              )}
              <Line type="monotone" dataKey="realRev" stroke={theme.palette.primary.main} strokeWidth={2} dot={false} name="Real/Projetado" />
              <Line type="monotone" dataKey="expectedRev" stroke={theme.palette.secondary.main} strokeDasharray="5 5" strokeWidth={2} dot={false} name="Esperado" />
              <Legend verticalAlign="top" height={36} iconType="plainline" />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {/* Bottom Chart: Cumulative Loss */}
      <Box sx={{ border: 1, borderColor: theme.palette.divider, p: 2, borderRadius: 1, height: 200 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Resultado Financeiro Acumulado (Real vs Esperado)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Diferença acumulada entre o valor obtido e o valor esperado (prejuízo ou lucro acumulado).
        </Typography>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={displayData} margin={{ top: 5, right: 20, bottom: 20, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={true} stroke={theme.palette.divider} />
            <XAxis 
              dataKey={hasRealData ? "date" : "dim"} 
              label={{ value: hasRealData ? 'Data' : 'DIM', position: 'insideBottom', offset: -10 }} 
              tickLine={false}
              axisLine={true}
              interval={Math.floor(displayData.length / 10)}
            />
            <YAxis 
              label={{ value: 'Delta R$', angle: -90, position: 'insideLeft' }} 
              tickLine={false}
              axisLine={true}
            />
            <Tooltip labelFormatter={(label) => hasRealData ? `Data: ${label}` : `DIM: ${label}`} />
            {eventRange && (
              <ReferenceArea 
                x1={hasRealData ? displayData.find(d => d.dim === eventRange.start)?.date : eventRange.start} 
                x2={hasRealData ? displayData.find(d => d.dim === eventRange.end)?.date : eventRange.end} 
                fill={theme.palette.action.selected} 
                fillOpacity={0.5} 
              />
            )}
            {projectionStartDim && (
              <ReferenceArea 
                x1={hasRealData ? displayData.find(d => d.dim === projectionStartDim)?.date : projectionStartDim} 
                x2={hasRealData ? displayData[displayData.length - 1].date : displayData[displayData.length - 1].dim} 
                fill={theme.palette.info.light} 
                fillOpacity={0.1}
                label="Projeção"
              />
            )}
            <ReferenceLine y={0} stroke={theme.palette.text.secondary} strokeDasharray="3 3" />
            <Line type="monotone" dataKey="cumulativeLoss" stroke={theme.palette.primary.main} strokeWidth={2} dot={false} name="Balanço Acumulado" />
          </LineChart>
        </ResponsiveContainer>
      </Box>

    </Box>
  );
};

// Mock Data Generator (preserved for fallback)
const generateCowData = () => {
  const data = [];
  let cumulativeLoss = 0;
  const milkPrice = 2.35;
  
  for (let dim = 0; dim <= 150; dim++) {
    const a = 15; 
    const b = 0.2; 
    const c = 0.004; 
    let expectedVol = a * Math.pow(Math.max(dim, 1), b) * Math.exp(-c * dim) * 1.8; 
    const noise = (Math.sin(dim * 0.5) * 0.5) + ((Math.random() - 0.5) * 1.5);
    let realVol = expectedVol + noise;
    
    let isEvent = false;
    if (dim >= 55 && dim < 55 + 18) {
       isEvent = true;
       const daysIntoEvent = dim - 55;
       const severity = 0.35; 
       const recovery = daysIntoEvent / 18; 
       const impact = 1 - (severity * (1 - Math.pow(recovery, 2))); 
       realVol = expectedVol * impact + noise;
    } else if (dim >= 55 + 18) {
       realVol = realVol * 0.96; 
    }

    const expectedRev = expectedVol * milkPrice;
    const realRev = realVol * milkPrice;
    const diffRev = realRev - expectedRev;
    if (dim >= 55) {
        cumulativeLoss += diffRev;
    }

    data.push({
      dim,
      dateObj: new Date(), // Mock date object
      date: `D${dim}`, // Placeholder for mock
      fullDate: `Day ${dim}`,
      expectedVol: Number(expectedVol.toFixed(1)),
      realVol: Number(realVol.toFixed(1)),
      expectedRev: Number(expectedRev.toFixed(1)),
      realRev: Number(realRev.toFixed(1)),
      cumulativeLoss: Number(cumulativeLoss.toFixed(1)),
      isEvent,
      isProjection: false,
      notes: ''
    });
  }
  return data;
};

export default MilkProductionEconomicsView;
